import { Layer, ThemeContext } from 'grommet';
import { ComponentChildren } from 'preact';

const layerTheme = {
  layer: {
    background: 'var(--primaryGreen)',
    border: { radius: '16px' },
    container: { extend: 'overflow: hidden;' },
  },
};

interface WelcomeModalLayoutProps {
  /** The green band: artwork, title and whatever else a tour's opening needs. */
  hero: ComponentChildren;
  mascotSrc: string;
  speech: string;
  buttonLabel: string;
  onStart: () => void;
}

/**
 * The welcome-modal skeleton shared by the guided journey and the TZ+ tour: a green hero band over
 * a yellow band carrying the mascot, its speech bubble and the single call to action.
 *
 * The hero is a slot rather than a set of props on purpose. The two tours' heroes differ in artwork,
 * alignment, weight and whether there is a partner logo at all — five props' worth of divergence for
 * two callers, where a slot lets each simply say what it looks like.
 *
 * Deliberately not dismissible: no `onEsc`, no `onClickOutside`. The button is the only way out,
 * which is how the guided journey's welcome has always behaved.
 */
const WelcomeModalLayout = ({ hero, mascotSrc, speech, buttonLabel, onStart }: WelcomeModalLayoutProps) => (
  <ThemeContext.Extend value={layerTheme}>
    <Layer animation="fadeIn" responsive={false}>
      <div className="preflight relative w-[1036px]">
        {hero}

        <div className="preflight relative h-[208px] bg-[#FFDF6C]">
          <div className="preflight pointer-events-none absolute left-0 top-[-112px] h-[320px] w-[323px] overflow-hidden">
            <img src={mascotSrc} alt="Zoolife mascot" className="preflight h-full w-full object-contain" />
          </div>

          {/* Sized to its text rather than pinned to a fixed height, so the two tours' bubbles —
              89px and 113px of copy in the designs — each sit correctly without a per-tour metric. */}
          <div className="preflight absolute left-[326px] top-[27px] flex min-h-[101px] w-[631px] items-center rounded-[10px] bg-white px-4">
            <div className="preflight absolute left-[-11px] top-[33px] h-0 w-0 border-y-[10px] border-r-[13px] border-y-transparent border-r-white" />
            <p className="preflight preflight-p text-lg font-bold leading-[1.3] text-[#1c1917]">{speech}</p>
          </div>

          <button
            type="button"
            onClick={onStart}
            className="preflight preflight-button absolute left-[326px] top-[142px] flex h-8 items-center justify-center whitespace-nowrap rounded-full bg-primary-green px-4 py-2 text-base font-bold leading-none text-white"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </Layer>
  </ThemeContext.Extend>
);

export default WelcomeModalLayout;
